import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined';
import { isCustomer } from '../../../api';
import { trackEvent } from '../../../analytics';
import { getHangoutInvite, respondToHangoutInvite, toApiError } from '../api';
import { usePublicHangoutInvite } from '../hooks';
import { HangoutInvite, InviteResponseStatus } from '../types';
import { canRespond, errorTitle, formatInviteDate, formatInviteTime, inviteTypeLabel, loginUrlFor, takePendingAction } from '../utils';
import { Avatar, ErrorState, InviteStatusBadge, Skeleton, VenueImage } from '../components/common';
import { ShareInviteSheet } from '../components/ShareInviteSheet';
import '../hangouts.css';

// Public landing page for shared links (/invite/:publicToken). Anyone can see the public details;
// responding needs a customer login, after which the visitor returns here and the action completes.
const PublicInvitePage: React.FC = () => {
  const { publicToken } = useParams<{ publicToken: string }>();
  const [searchParams] = useSearchParams();
  const ref = searchParams.get('ref');
  const navigate = useNavigate();
  const signedIn = isCustomer();
  const { data: invite, error, loading, reload } = usePublicHangoutInvite(publicToken, ref, signedIn ? 'customer' : 'anon');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const [full, setFull] = useState<HangoutInvite | null>(null); // for share permissions / ref code
  const [sharing, setSharing] = useState(false);
  const opened = useRef(false);
  const pendingDone = useRef(false);
  const shareData = useMemo(() => invite && ({ venue: invite.venue, offer: invite.offer, inviteDateTime: invite.inviteDateTime, host: invite.host, message: invite.message }), [invite]);

  useEffect(() => {
    if (invite && !opened.current) {
      opened.current = true;
      trackEvent('hangout_invite_opened', { invite_type: invite.inviteType, signed_in: signedIn, via_referral: !!ref });
    }
  }, [invite, signedIn, ref]);

  // Members also get the full view (share permission and their own referral code).
  useEffect(() => {
    if (invite?.id) getHangoutInvite(invite.id).then(setFull).catch(() => setFull(null));
  }, [invite?.id, invite?.myStatus]);

  const respond = useCallback(async (status: InviteResponseStatus) => {
    if (!invite || !publicToken) return;
    if (!isCustomer() || !invite.id) {
      // Remember the page (with its referral) and the chosen action; complete it after sign-in.
      const returnUrl = `/invite/${publicToken}${ref ? `?ref=${encodeURIComponent(ref)}` : ''}`;
      navigate(loginUrlFor(returnUrl, status === 'Declined' ? undefined : { token: publicToken, action: status as 'Interested' | 'Going' | 'Requested' }));
      return;
    }
    setBusy(true);
    setNotice(null);
    try {
      await respondToHangoutInvite(invite.id, status);
      const event = status === 'Going' ? 'hangout_invite_joined' : status === 'Requested' ? 'hangout_join_requested' : status === 'Interested' ? 'hangout_invite_interested' : 'hangout_invite_declined';
      trackEvent(event, { invite_type: invite.inviteType });
      setNotice({
        ok: true,
        text: status === 'Going' ? "You're going! 🎉 See you there." : status === 'Requested' ? 'Request sent. The host will approve you soon.'
          : status === 'Interested' ? "Marked as interested. We'll keep you posted." : "Got it, you're not going."
      });
      await reload();
    } catch (err) {
      const e = toApiError(err);
      setNotice({ ok: e.code === 'already_joined', text: e.message });
      await reload();
    } finally {
      setBusy(false);
    }
  }, [invite, publicToken, ref, navigate, reload]);

  // Back from sign-in: finish what the visitor tapped before logging in.
  useEffect(() => {
    if (!invite || !publicToken || pendingDone.current || !signedIn || !invite.id) return;
    pendingDone.current = true;
    const action = takePendingAction(publicToken);
    if (action && canRespond(invite) && invite.myStatus !== 'Going' && invite.myStatus !== action) respond(action);
  }, [invite, publicToken, signedIn, respond]);

  if (loading && !invite) {
    return <main className="hg"><div className="hg-wrap" style={{ display: 'grid', gap: 12 }}><Skeleton height={260} /><Skeleton height={40} width="70%" /><Skeleton height={120} /></div></main>;
  }
  if (error || !invite) {
    const e = errorTitle(error);
    return <main className="hg"><div className="hg-wrap"><ErrorState title={e.title} body={e.body} /></div></main>;
  }

  const blocked = invite.joinBlockedReason ? errorTitle(null, invite.joinBlockedReason) : null;
  const isPrivate = invite.inviteType === 'PrivateGroup';
  const me = invite.myStatus;
  const going = me === 'Going' && invite.isApproved;
  const place = [invite.venue.area, invite.venue.city].filter(Boolean).join(', ');

  return (
    <main className="hg">
      <div className="hg-wrap">
        <div className="hg-invite" style={{ marginTop: 4 }}>
          <VenueImage url={invite.venue.imageUrl} alt={invite.venue.name} className="hg-invite-photo" />
          <div className="hg-invite-body">
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
              <p className="hg-invited" style={{ margin: 0 }}>You're Invited! 🎉</p>
              {invite.status !== 'Active' && <InviteStatusBadge status={invite.status} />}
            </div>
            <h2 style={{ marginTop: 6 }}>Night Out at<br />{invite.venue.name}</h2>
            <div className="hg-meta"><CalendarMonthOutlinedIcon />{formatInviteDate(invite.inviteDateTime)} &nbsp;|&nbsp; {formatInviteTime(invite.inviteDateTime)}</div>
            <div className="hg-meta"><LocationOnOutlinedIcon />{place}</div>
            {invite.offer && <div className="hg-offer-chip"><LocalOfferOutlinedIcon fontSize="small" />{invite.offer.title}{invite.offer.benefit ? ` · ${invite.offer.benefit}` : ''}</div>}
            <div className="hg-inviter">
              <Avatar initials={invite.host.initials} />
              <div><small>Invited by</small><b>{invite.host.displayName}</b></div>
            </div>
            {invite.message && <p className="hg-invite-msg">{invite.message}</p>}
            <div className="hg-counts" style={{ marginTop: 14, fontSize: 14 }}>
              <b>{invite.goingCount} Going</b><em>{invite.interestedCount} Interested</em><span className="hg-muted">· {inviteTypeLabel(invite.inviteType)}</span>
            </div>
          </div>
        </div>

        {notice && <div className={`hg-notice ${notice.ok ? 'success' : 'error'}`} role="status">{notice.text}</div>}
        {blocked && <div className="hg-notice info" role="status"><b>{blocked.title}.</b> {blocked.body}</div>}
        {!signedIn && canRespond(invite) && (
          <p className="hg-muted" style={{ textAlign: 'center', fontSize: 13, marginTop: 14 }}>
            Sign in with your mobile number to respond. It takes a few seconds.
          </p>
        )}
        {going && <p style={{ textAlign: 'center', color: '#4ade80', fontWeight: 700, marginTop: 14 }}>You're going 🎉</p>}
        {me === 'Requested' && <p style={{ textAlign: 'center', color: '#F5C542', fontWeight: 700, marginTop: 14 }}>Request sent · waiting for the host to approve</p>}
        {me === 'Interested' && <p style={{ textAlign: 'center', color: '#F5C542', fontWeight: 700, marginTop: 14 }}>You're interested</p>}
      </div>

      <div className="hg-sticky">
        <div className="hg-wrap">
          {invite.isHost ? (
            <div style={{ display: 'flex', gap: 10 }}>
              <Link className="hg-btn hg-outline" style={{ flex: 1 }} to={`/invites/${invite.id}/responses`}>Responses</Link>
              <button type="button" className="hg-btn hg-gold" style={{ flex: 1 }} onClick={() => setSharing(true)} disabled={invite.status !== 'Active'}>Share Invite</button>
            </div>
          ) : going ? (
            <>
              {invite.canChat && invite.id && <Link className="hg-btn hg-gold block" to={`/invites/${invite.id}/chat`}><ChatBubbleOutlineIcon />Open Group Chat</Link>}
              <div style={{ display: 'flex', gap: 10 }}>
                {full?.canShare && <button type="button" className="hg-btn hg-outline" style={{ flex: 1 }} onClick={() => setSharing(true)}><GroupAddOutlinedIcon />Invite friends</button>}
                {canRespond(invite) && <button type="button" className="hg-btn hg-danger" style={{ flex: 1 }} disabled={busy} onClick={() => respond('Declined')}>Can't go</button>}
              </div>
            </>
          ) : canRespond(invite) ? (
            me === 'Requested' ? (
              <button type="button" className="hg-btn hg-outline block" disabled={busy} onClick={() => respond('Declined')}>Cancel request</button>
            ) : (
              <div style={{ display: 'flex', gap: 10 }}>
                {me !== 'Interested' && <button type="button" className="hg-btn hg-gold-outline" style={{ flex: 1 }} disabled={busy} onClick={() => respond('Interested')}>Interested</button>}
                <button type="button" className="hg-btn hg-gold" style={{ flex: 1 }} disabled={busy} onClick={() => respond(isPrivate ? 'Requested' : 'Going')}>
                  {isPrivate ? 'Request to Join' : 'Join Hangout'}
                </button>
              </div>
            )
          ) : (
            <Link className="hg-btn hg-outline block" to="/explore">Explore other venues</Link>
          )}
        </div>
      </div>

      {sharing && invite.id && shareData && (full || invite.isHost) && (
        <ShareInviteSheet
          invite={shareData}
          publicToken={invite.publicToken}
          refCode={invite.isHost ? null : full?.myRefCode}
          onClose={() => setSharing(false)}
        />
      )}
    </main>
  );
};

export default PublicInvitePage;
